// Van Life — bot logic. getBotAction is the heuristic (medium) bot;
// getPerfectBotAction sees all hands + the deck sequence and plans the match.

import {
  Card, Suit, ZoneCardId, ChallengeCardId,
  CompletedTrick, VanLifeMatchState, VanLifeState, VanLifeAction,
} from './types';
import { DEAL_SIZE, HAND_SIZE, CHALLENGE_DIFFICULTY, zoneValidFinals } from './data';
import { getValidActions } from './rules';
// Circular at module scope but only used inside functions (deferred), so it resolves fine.
import { applyAction } from './van-life';

// ── Heuristic helpers ─────────────────────────────────────────────────────────

// Returns whether the challenge wants this trick won (true), thrown (false),
// or doesn't care (null).
function challengeWinOverride(
  challenge: ChallengeCardId,
  playerId: string,
  m: VanLifeMatchState,
): boolean | null {
  const { completedTricks, trickNumber: trickNum, totalTricks } = m;
  const myWins  = completedTricks.filter(t => t.winnerId === playerId);
  const wonNums = myWins.map(t => t.trickNumber);
  const half    = Math.floor(totalTricks / 2);

  switch (challenge) {
    case 'first-blood':   return trickNum === 1 ? true : null;
    case 'last-stand':    return trickNum === totalTricks ? true : null;
    case 'bookends':      return (trickNum === 1 || trickNum === totalTricks) ? true : null;
    case 'alternating':   return wonNums.includes(trickNum - 1) ? false : null;
    case 'streak': {
      let run = 0;
      for (let n = trickNum - 1; wonNums.includes(n) && n >= 1; n--) run++;
      return run >= 2 ? true : null;
    }
    case 'iron-patience': return trickNum <= 3 ? false : null;
    case 'odd-tricks':    return trickNum % 2 === 1 ? true : false;
    case 'strong-finish':
      if (trickNum > half) return true;
      if (myWins.filter(t => t.trickNumber <= half).length >= 2) return false;
      return null;
    case 'deck-beater':    return true;
    case 'double-run': {
      let run = 0;
      for (let n = trickNum - 1; wonNums.includes(n) && n >= 1; n--) run++;
      return run >= 1 ? true : null;
    }
    case 'pack-light':
      if (myWins.length > 5) return false;
      return null;
    case 'first-or-last':  return (trickNum === 1 || trickNum === totalTricks) ? true : null;
    case 'low-gear':       return null;
    case 'early-bird':     return trickNum <= half ? true : null;
    case 'night-owl':      return trickNum > half ? true : null;
    case 'pioneer-spirit': return myWins.length === 0 ? true : null;
    default:               return null;
  }
}

// Score a card for discard purposes: higher = keep.
// wantToWin = we want to win tricks (keep power); !wantToWin = want to lose (keep low cards).
function scoreCardForKeep(card: Card, wantToWin: boolean, challenge: ChallengeCardId): number {
  if (card.suit === 'Wild') return 1000 + card.rank; // always keep wilds
  if (challenge === 'low-road' || challenge === 'low-gear') {
    // Keep low-rank cards to win with them
    const threshold = challenge === 'low-road' ? 2 : 4;
    if (card.rank <= threshold) return 900 + (threshold - card.rank); // strongly prefer low
    return wantToWin ? card.rank : -card.rank;
  }
  return wantToWin ? card.rank : -card.rank;
}

function botSelectCard(
  playable: Card[],
  deckCard: Card | null,
  leadCard: Card | null,
  wantToWin: boolean,
  m: VanLifeMatchState,
  playerId: string,
  challenge: ChallengeCardId,
  deckLeads: boolean,
): Card {
  if (playable.length === 1) return playable[0];

  // Player-led trick and this bot is the lead player: free choice of suit.
  // Deck card is face-down when player leads, so medium bot plays blind.
  if (!deckLeads && leadCard === null) {
    if (wantToWin) return playable.reduce((a, b) => a.rank > b.rank ? a : b);
    const nonWilds = playable.filter(c => c.suit !== 'Wild');
    return (nonWilds.length > 0 ? nonWilds : playable).reduce((a, b) => a.rank < b.rank ? a : b);
  }

  // Deck-led or player-led + bot is follow player. deckLeads implies deckCard is non-null.
  const ledSuit   = deckLeads && deckCard ? deckCard.suit : leadCard!.suit;
  const isWildLed = ledSuit === 'Wild';
  const inSuit    = playable.filter(c => c.suit === ledSuit);
  const wilds     = playable.filter(c => c.suit === 'Wild');
  const offSuit   = playable.filter(c => c.suit !== ledSuit && c.suit !== 'Wild');

  const deckInSuitRank = (!isWildLed && deckCard && deckCard.suit === ledSuit) ? deckCard.rank : 0;
  const leadInSuitRank = (!isWildLed && leadCard !== null && leadCard.suit === ledSuit) ? leadCard.rank : 0;
  const leadWildRank   = (isWildLed  && leadCard !== null && leadCard.suit === 'Wild')  ? leadCard.rank : 0;
  const deckWildRank   = (isWildLed  && deckCard && deckCard.suit === 'Wild') ? deckCard.rank : 0;
  const mustBeat = isWildLed
    ? Math.max(deckWildRank, leadWildRank)
    : Math.max(deckInSuitRank, leadInSuitRank);

  if (wantToWin) {
    if (isWildLed) {
      const winWilds = wilds.filter(c => c.rank > mustBeat);
      if (winWilds.length > 0) return winWilds.reduce((a, b) => a.rank < b.rank ? a : b);
      return (wilds.length > 0 ? wilds : playable).reduce((a, b) => a.rank < b.rank ? a : b);
    }
    if (challenge === 'low-road' || challenge === 'low-gear') {
      const threshold = challenge === 'low-road' ? 2 : 4;
      const hasLow = m.completedTricks.filter(t => t.winnerId === playerId).some(t => t.winnerCard.rank <= threshold);
      if (!hasLow) {
        const lowWin = inSuit.filter(c => c.rank <= threshold && c.rank > mustBeat);
        if (lowWin.length > 0) return lowWin[0];
      }
    }
    const winners = inSuit.filter(c => c.rank > mustBeat);
    if (winners.length > 0) return winners.reduce((a, b) => a.rank < b.rank ? a : b);
    if (wilds.length > 0) return wilds.reduce((a, b) => a.rank > b.rank ? a : b);
    return (inSuit.length > 0 ? inSuit : playable).reduce((a, b) => a.rank > b.rank ? a : b);
  }

  // Throw: off-suit discard first
  if (offSuit.length > 0) {
    if (challenge === 'suit-sampler') {
      const wonSuits = new Set(
        m.completedTricks.filter(t => t.winnerId === playerId && t.winnerCard.suit !== 'Wild')
          .map(t => t.winnerCard.suit as Suit)
      );
      const unneeded = offSuit.filter(c => wonSuits.has(c.suit as Suit));
      if (unneeded.length > 0) return unneeded.reduce((a, b) => a.rank < b.rank ? a : b);
    }
    return offSuit.reduce((a, b) => a.rank < b.rank ? a : b);
  }
  if (inSuit.length > 0) {
    const losers = inSuit.filter(c => c.rank <= mustBeat);
    return (losers.length > 0 ? losers : inSuit).reduce((a, b) => a.rank < b.rank ? a : b);
  }
  return wilds.reduce((a, b) => a.rank < b.rank ? a : b);
}

// ── Movement (shared by medium + perfect) ─────────────────────────────────────
// Winning needs all 8 parks conquered, so coverage matters more than raw odds:
// strongly prefer unconquered parks, prefer ones easy for this player's role, and
// avoid doubling up on the park the partner is already heading to (the second mover
// sees the first's pending move, so the pair spreads out to cover two parks).
function chooseMoveAction(
  state: VanLifeState,
  playerId: string,
  valid: VanLifeAction[],
): VanLifeAction {
  const moves = valid.filter(a => a.type === 'chooseMove') as Extract<VanLifeAction, { type: 'chooseMove' }>[];
  const player  = state.players.find(p => p.id === playerId)!;
  const isTrack = player.playerNumber === 1;
  const partner = state.players.find(p => p.id !== playerId);
  const partnerTarget = partner ? state.pendingMoves[partner.id] : undefined;

  let best = moves[0], bestScore = -Infinity;
  for (const move of moves) {
    const park = state.parks.find(p => p.id === move.targetParkId)!;
    let score: number;
    if (park.conquered) {
      score = -100; // never re-conquer when an open park is reachable
    } else {
      score = 50; // strongly prefer unconquered parks
      score += isTrack
        ? zoneValidFinals(park.zoneCardId).length          // more valid finals = easier zone
        : (CHALLENGE_DIFFICULTY[park.challengeCardId] ?? 5); // higher = easier challenge
      // Spread out: don't both target the same open park (costs us a distinct conquest).
      if (partnerTarget !== undefined && move.targetParkId === partnerTarget) score -= 40;
    }
    if (score > bestScore) { bestScore = score; best = move; }
  }
  return best;
}

// ── getBotAction (heuristic bot) ──────────────────────────────────────────────

export function getBotAction(state: VanLifeState, playerId: string): VanLifeAction | null {
  const valid = getValidActions(state, playerId);
  if (valid.length === 0) return null;

  const player   = state.players.find(p => p.id === playerId)!;
  const isTrack  = player.playerNumber === 1;

  if (state.phase === 'movement') return chooseMoveAction(state, playerId, valid);

  if (state.phase === 'trick-taking' && state.match) {
    const m      = state.match;
    const myPark = state.parks.find(p => p.id === player.currentParkId)!;
    const p1     = state.players.find(p => p.playerNumber === 1)!;
    const p1Park = state.parks.find(p => p.id === p1.currentParkId)!;

    // ── Discard phase ──
    if (m.subPhase === 'discard' && !m.pendingDiscards[playerId]) {
      const hand = m.hands[playerId] ?? [];
      const numToDiscard = DEAL_SIZE - HAND_SIZE;

      // Decide if we want to win tricks based on objective
      let wantToWin: boolean;
      if (isTrack) {
        const validFinals = zoneValidFinals(myPark.zoneCardId);
        const target = validFinals.reduce((best, pos) =>
          Math.abs(pos - m.tracker) < Math.abs(best - m.tracker) ? pos : best);
        wantToWin = target < m.tracker; // P1 wins push tracker left
      } else {
        const ov = challengeWinOverride(myPark.challengeCardId, playerId, m);
        wantToWin = ov !== null ? ov : true;
      }

      const challenge = isTrack ? 'pioneer-spirit' as ChallengeCardId : myPark.challengeCardId;
      const scored = hand
        .map(c => ({ id: c.id, score: scoreCardForKeep(c, wantToWin, challenge) }))
        .sort((a, b) => a.score - b.score); // lowest = first to discard

      return {
        type: 'discardCards',
        playerId,
        cardIds: scored.slice(0, numToDiscard).map(s => s.id),
      };
    }

    if (m.subPhase === 'discard') return null;

    const playable = (valid.filter(a => a.type === 'playCard') as Extract<VanLifeAction, { type: 'playCard' }>[])
      .map(a => m.hands[playerId].find(c => c.id === a.cardId)!)
      .filter(Boolean);

    let wantToWin: boolean;

    if (isTrack) {
      const validFinals = zoneValidFinals(myPark.zoneCardId);
      const target = validFinals.reduce((best, pos) =>
        Math.abs(pos - m.tracker) < Math.abs(best - m.tracker) ? pos : best);
      const avoidExtremes = myPark.zoneCardId === 'safe-passage';
      if (avoidExtremes && m.tracker === 2) wantToWin = false;
      else if (avoidExtremes && m.tracker === 6) wantToWin = true;
      else {
        const delta = target - m.tracker;
        wantToWin = delta < 0;
      }
    } else {
      const ov = challengeWinOverride(myPark.challengeCardId, playerId, m);
      if (ov !== null) {
        wantToWin = ov;
      } else {
        const validFinals = zoneValidFinals(p1Park.zoneCardId);
        const target = validFinals.reduce((best, pos) =>
          Math.abs(pos - m.tracker) < Math.abs(best - m.tracker) ? pos : best);
        const delta = target - m.tracker;
        wantToWin = delta > 0;
      }
    }

    const leadCard = m.subPhase === 'follow' ? m.leadCard : null;
    const chosen   = botSelectCard(playable, m.currentDeckCard, leadCard, wantToWin, m, playerId,
      isTrack ? 'pioneer-spirit' : myPark.challengeCardId, m.deckLeads);
    return { type: 'playCard', playerId, cardId: chosen.id };
  }

  return valid[0];
}

// ── Perfect-knowledge bot ─────────────────────────────────────────────────────
// Sees the full remaining deck sequence and partner's hand; plans the entire
// remaining match in advance to optimally satisfy zone + challenge conditions.

function canWinAgainstDeck(hand: Card[], dc: Card | null): boolean {
  if (dc === null) return hand.length > 0; // deck exhausted — any play wins vs no opponent
  if (dc.suit === 'Wild') return hand.some(c => c.suit === 'Wild' && c.rank > dc.rank);
  const inSuit = hand.filter(c => c.suit === dc.suit);
  if (inSuit.length > 0) return inSuit.some(c => c.rank > dc.rank);
  return hand.some(c => c.suit === 'Wild');
}

function scheduleChallengeAssignments(
  ch: ChallengeCardId,
  pid: string,
  canWin: boolean[],
  wonNums: number[],
  offset: number, n: number, total: number, half: number,
  tryAssign: (i: number, pid: string) => boolean,
): void {
  const wonSet  = new Set(wonNums);
  const idx     = (tn: number) => tn - offset - 1;
  const inRange = (tn: number) => tn > offset && tn <= offset + n;

  switch (ch) {
    case 'first-blood':
      if (!wonSet.has(1) && inRange(1)) tryAssign(idx(1), pid);
      break;
    case 'last-stand':
      if (!wonSet.has(total) && inRange(total)) tryAssign(idx(total), pid);
      break;
    case 'bookends':
      if (!wonSet.has(1) && inRange(1)) tryAssign(idx(1), pid);
      if (!wonSet.has(total) && inRange(total)) tryAssign(idx(total), pid);
      break;
    case 'first-or-last':
      if (!wonSet.has(1) && !wonSet.has(total)) {
        if (!(inRange(1) && tryAssign(idx(1), pid)) && inRange(total)) tryAssign(idx(total), pid);
      }
      break;
    case 'streak':
    case 'double-run': {
      const need = ch === 'streak' ? 3 : 2;
      for (let i = 0; i <= n - need; i++) {
        if (canWin.slice(i, i + need).every(Boolean)) {
          for (let j = i; j < i + need; j++) tryAssign(j, pid);
          break;
        }
      }
      break;
    }
    case 'odd-tricks':
      for (let i = 0; i < n; i++) if ((offset + i + 1) % 2 === 1 && canWin[i]) tryAssign(i, pid);
      break;
    case 'early-bird': {
      const hasEarly = wonNums.some(w => w <= half);
      if (!hasEarly) for (let i = 0; i < n; i++)
        if ((offset + i + 1) <= half && canWin[i] && tryAssign(i, pid)) break;
      break;
    }
    case 'night-owl': {
      const hasLate = wonNums.some(w => w > half);
      if (!hasLate) for (let i = 0; i < n; i++)
        if ((offset + i + 1) > half && canWin[i] && tryAssign(i, pid)) break;
      break;
    }
    case 'pioneer-spirit':
      if (wonNums.length === 0) for (let i = 0; i < n; i++) if (canWin[i] && tryAssign(i, pid)) break;
      break;
    case 'strong-finish': {
      const fh = wonNums.filter(w => w <= half).length;
      const sh = wonNums.filter(w => w > half).length;
      if (sh <= fh) for (let i = 0; i < n; i++)
        if ((offset + i + 1) > half && canWin[i]) tryAssign(i, pid);
      break;
    }
    case 'deck-beater':
      for (let i = 0; i < n; i++) if (canWin[i]) tryAssign(i, pid);
      break;
    case 'pack-light': {
      let got = 0, need = Math.max(0, 2 - wonNums.length);
      for (let i = 0; i < n && got < need; i++) if (canWin[i] && tryAssign(i, pid)) got++;
      break;
    }
    default:
      if (wonNums.length === 0) for (let i = 0; i < n; i++) if (canWin[i] && tryAssign(i, pid)) break;
      break;
  }
}

function computePerfectPlan(
  deckSeq: (Card | null)[],
  p1Hand: Card[], p2Hand: Card[],
  p1Zone: ZoneCardId,
  p2Ch: ChallengeCardId,
  tracker: number,
  done: CompletedTrick[],
  p1Id: string, p2Id: string,
): string[] {
  const n      = deckSeq.length;
  const offset = done.length;
  const total  = offset + n;
  const half   = Math.floor(total / 2);
  const plan   = new Array<string>(n).fill('deck');

  const p1cw = deckSeq.map(dc => canWinAgainstDeck(p1Hand, dc));
  const p2cw = deckSeq.map(dc => canWinAgainstDeck(p2Hand, dc));

  const p2wonNums = done.filter(t => t.winnerId === p2Id).map(t => t.trickNumber);

  const tryAssign = (i: number, pid: string): boolean => {
    if (i < 0 || i >= n) return false;
    if (!(pid === p1Id ? p1cw[i] : p2cw[i])) return false;
    if (plan[i] !== 'deck') return false;
    plan[i] = pid;
    return true;
  };

  scheduleChallengeAssignments(p2Ch, p2Id, p2cw, p2wonNums, offset, n, total, half, tryAssign);

  const validFinals = zoneValidFinals(p1Zone);
  const target = validFinals.reduce((best, pos) =>
    Math.abs(pos - tracker) < Math.abs(best - tracker) ? pos : best);
  const p1planned = () => plan.filter(x => x === p1Id).length;
  const p2planned = () => plan.filter(x => x === p2Id).length;
  let need = (target - tracker) - (p2planned() - p1planned());

  for (let i = 0; i < n && need !== 0; i++) {
    if (plan[i] !== 'deck') continue;
    if (need > 0 && p2cw[i]) { plan[i] = p2Id; need--; }
    else if (need < 0 && p1cw[i]) { plan[i] = p1Id; need++; }
  }

  return plan;
}

// Base full-knowledge policy: plan who wins each remaining trick (zone + challenge
// aware, using the known deck order and both hands) and play the matching card.
// Used both as a fast standalone policy and as the rollout policy for the lookahead.
function perfectPolicyAction(state: VanLifeState, playerId: string): VanLifeAction | null {
  const valid = getValidActions(state, playerId);
  if (valid.length === 0) return null;

  if (state.phase === 'movement') return chooseMoveAction(state, playerId, valid);
  if (state.phase !== 'trick-taking' || !state.match) return valid[0];

  const m = state.match;
  if (m.subPhase === 'discard') return getBotAction(state, playerId);

  const me     = state.players.find(p => p.id === playerId)!;
  const myPark = state.parks.find(p => p.id === me.currentParkId)!;
  const p1     = state.players.find(p => p.playerNumber === 1)!;
  const p2     = state.players.find(p => p.playerNumber === 2)!;
  const p1Park = state.parks.find(p => p.id === p1.currentParkId)!;
  const p2Park = state.parks.find(p => p.id === p2.currentParkId)!;

  const n       = m.totalTricks - m.trickNumber + 1;
  const deckSeq: (Card | null)[] = [m.currentDeckCard, ...m.parkDeck].slice(0, n);
  while (deckSeq.length < n) deckSeq.push(null); // pad exhausted tricks to keep indices aligned

  const plan = computePerfectPlan(
    deckSeq,
    m.hands[p1.id], m.hands[p2.id],
    p1Park.zoneCardId,
    p2Park.challengeCardId,
    m.tracker, m.completedTricks,
    p1.id, p2.id,
  );

  const iShouldWin = plan[0] === playerId;
  const leadCard   = m.subPhase === 'follow' ? m.leadCard : null;
  const playable   = (valid.filter(a => a.type === 'playCard') as Extract<VanLifeAction, {type:'playCard'}>[])
    .map(a => m.hands[playerId].find(c => c.id === a.cardId)!).filter(Boolean);

  const isTrack = me.playerNumber === 1;
  const chosen = botSelectCard(playable, m.currentDeckCard, leadCard, iShouldWin, m, playerId,
    isTrack ? 'pioneer-spirit' : myPark.challengeCardId, m.deckLeads);
  return { type: 'playCard', playerId, cardId: chosen.id };
}

// Who acts next during trick-taking (discard, lead, or follow).
function activePlayerId(state: VanLifeState): string {
  const m = state.match!;
  if (m.subPhase === 'discard') {
    return state.players.find(p => !m.pendingDiscards[p.id])!.id;
  }
  const followId = state.players.find(p => p.id !== m.leadPlayerId)!.id;
  return m.subPhase === 'lead' ? m.leadPlayerId : followId;
}

// Score a finished match: distinct parks conquered is what wins the game, so weight
// it heavily; break ties by total conditions met (more robust / both-player success).
function scoreMatchResult(state: VanLifeState): number {
  const r = state.match?.result;
  if (!r) return 0;
  const distinctConquered = new Set(r.parkResults.filter(x => x.conquered).map(x => x.parkId)).size;
  const condsMet = r.parkResults.reduce((s, x) => s + (x.zoneMet ? 1 : 0) + (x.challengeMet ? 1 : 0), 0);
  return distinctConquered * 1000 + condsMet;
}

// Play the rest of the match out with the base policy (both players) and score it.
function rolloutScore(state: VanLifeState): number {
  let s = state;
  let guard = 200;
  while (s.phase === 'trick-taking' && s.match && guard-- > 0) {
    const a = perfectPolicyAction(s, activePlayerId(s));
    if (!a) break;
    const next = applyAction(s, a);
    if (next === s) break; // no progress — bail out
    s = next;
  }
  return scoreMatchResult(s);
}

// Perfect bot: one-step lookahead over every legal action (discard combo or card),
// each evaluated by simulating to the end of the match with the full-knowledge
// rollout policy. This dominates the bare policy — it can't do worse than rolling
// out the policy move, and it actively shops for a better discard / card.
export function getPerfectBotAction(state: VanLifeState, playerId: string): VanLifeAction | null {
  const valid = getValidActions(state, playerId);
  if (valid.length === 0) return null;

  if (state.phase === 'movement') return chooseMoveAction(state, playerId, valid);
  if (state.phase !== 'trick-taking' || !state.match) return valid[0];

  let best = valid[0], bestScore = -Infinity;
  for (const a of valid) {
    const next = applyAction(state, a);
    if (next === state) continue;
    const score = rolloutScore(next);
    if (score > bestScore) { bestScore = score; best = a; }
  }
  return best;
}
